import { Cliente, FormulaPigmentacao, MovimentoFinanceiro, Orcamento, Pigmento, PigmentoDetalhado } from '../types';

interface DashboardStats {
  clientes: { total: number };
  formulas: { total: number };
  orcamentos: { total: number; valor_total: number; aprovados: number; taxa_aprovacao: number };
  financeiro: { receitas: number; despesas: number; saldo: number };
}

// Mapeamento entre campos do frontend e backend
interface ClienteBackend {
  id: number;
  nome: string;
  email: string | null;
  telefone: string | null;
  cpf_cnpj: string | null;
  endereco_rua: string | null;
  endereco_numero: string | null;
  endereco_bairro: string | null;
  endereco_cidade: string | null;
  endereco_estado: string | null;
  endereco_cep: string | null;
  tipo_pessoa: 'fisica' | 'juridica';
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

interface FormulaBackend {
  id: number;
  nome: string;
  codigo: string;
  cor_final_hex: string | null;
  descricao: string | null;
  foto_amostra: string | null;
  rendimento: number;
  unidade_rendimento: string;
  custo_total: number;
  preco_sugerido: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  pigmentos?: {
    formula_pigmento_id: number;
    quantidade: number;
    unidade: string;
    custo_item: number;
    pigmento: {
      id: number;
      nome: string;
      codigo: string;
      cor_hex: string;
      preco_unitario: number;
    };
  }[];
}

interface PigmentoBackend {
  id: number;
  nome: string;
  codigo: string;
  cor_hex: string | null;
  categoria: string | null;
  unidade_medida: string;
  preco_unitario: number;
  estoque_atual: number;
  estoque_minimo: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

interface MovimentacaoBackend {
  id: number;
  descricao: string;
  valor: number;
  tipo: 'entrada' | 'saida';
  categoria_nome: string | null;
  categoria_cor: string | null;
  data_movimento: string;
  data_vencimento: string | null;
  status: 'pendente' | 'pago' | 'cancelado';
  forma_pagamento: string | null;
  cliente_nome: string | null;
  orcamento_numero: string | null;
  observacoes: string | null;
  created_at: string;
  updated_at: string;
}

interface OrcamentoBackend {
  id: number;
  numero: string;
  cliente_id: number;
  data_orcamento: string;
  data_validade: string;
  valor_total: number;
  desconto: number;
  valor_final: number;
  status: 'orcamento' | 'aprovado' | 'rejeitado' | 'expirado';
  observacoes: string | null;
  cliente_nome: string;
  cliente_email: string | null;
  cliente_telefone: string | null;
  created_at: string;
  updated_at: string;
  itens?: {
    id: number;
    descricao: string;
    quantidade: number;
    unidade: string;
    preco_unitario: number;
    subtotal: number;
    observacoes: string | null;
    formula_nome: string | null;
    formula_codigo: string | null;
    formula_cor: string | null;
  }[];
}

export class ApiService {
  // URL da API - usa variável de ambiente ou fallback para desenvolvimento
  private baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  private async request<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        headers: {
          ...this.getAuthHeaders(),
          ...options?.headers,
        },
        ...options,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        // Criar erro com fieldErrors se disponível
        const error: any = new Error(data.error || `HTTP error! status: ${response.status}`);
        if (data.fieldErrors) {
          error.fieldErrors = data.fieldErrors;
        }
        throw error;
      }

      return data.data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Funções de mapeamento
  private mapClienteFromBackend(cliente: ClienteBackend): Cliente {
    return {
      id: cliente.id,
      nome: cliente.nome,
      email: cliente.email || '',
      telefone: cliente.telefone || '',
      endereco: [
        cliente.endereco_rua,
        cliente.endereco_numero,
        cliente.endereco_bairro,
        cliente.endereco_cidade,
        cliente.endereco_estado,
        cliente.endereco_cep
      ].filter(Boolean).join(', '),
      cpfCnpj: cliente.cpf_cnpj || '',
      dataCadastro: cliente.created_at.split('T')[0] || cliente.created_at,
    };
  }

  private mapClienteToBackend(cliente: Omit<Cliente, 'id' | 'dataCadastro'>) {
    // Simplificar endereço - apenas colocar tudo em endereco_rua
    return {
      nome: cliente.nome,
      email: cliente.email || null,
      telefone: cliente.telefone || null,
      cpf_cnpj: cliente.cpfCnpj || null,
      endereco_rua: cliente.endereco || null,
      endereco_numero: null,
      endereco_bairro: null,
      endereco_cidade: null,
      endereco_estado: null,
      endereco_cep: null,
      tipo_pessoa: cliente.cpfCnpj?.includes('.') ? 'fisica' : 'juridica'
    };
  }

  private mapFormulaFromBackend(formula: FormulaBackend): FormulaPigmentacao {
    return {
      id: formula.id,
      nome: formula.nome,
      corFinal: formula.cor_final_hex || '#000000',
      descricao: formula.descricao || '',
      fotoAmostra: formula.foto_amostra || undefined,
      pigmentos: formula.pigmentos?.map(p => ({
        pigmentoId: p.pigmento.id,
        quantidade: p.quantidade,
        unidade: p.unidade
      })) || [],
      dataCriacao: formula.created_at.split('T')[0] || formula.created_at,
    };
  }

  private mapMovimentacaoFromBackend(mov: MovimentacaoBackend): MovimentoFinanceiro {
    return {
      id: mov.id,
      tipo: mov.tipo,
      descricao: mov.descricao,
      valor: mov.valor,
      data: mov.data_movimento,
      categoria: mov.categoria_nome || 'Sem categoria',
      clienteId: undefined, // Não temos o ID do cliente direto
      orcamentoId: undefined, // Não temos o ID do orçamento direto
    };
  }

  private mapOrcamentoFromBackend(orc: OrcamentoBackend): Orcamento {
    return {
      id: orc.id,
      clienteId: orc.cliente_id,
      cliente: {
        id: orc.cliente_id,
        nome: orc.cliente_nome,
        email: orc.cliente_email || '',
        telefone: orc.cliente_telefone || '',
        endereco: '',
        cpfCnpj: '',
        dataCadastro: '',
      },
      descricao: orc.observacoes || `Orçamento ${orc.numero}`,
      itens: orc.itens?.map((item, index) => ({
        id: item.id,
        descricao: item.descricao,
        quantidade: item.quantidade,
        valorUnitario: item.preco_unitario,
        valorTotal: item.subtotal,
      })) || [],
      valorTotal: orc.valor_final,
      dataOrcamento: orc.data_orcamento,
      dataVencimento: orc.data_validade,
      status: orc.status === 'aprovado' ? 'pedido' : orc.status as any,
      observacoes: orc.observacoes || undefined,
    };
  }

  // Cliente methods
  async getClientes(): Promise<Cliente[]> {
    const clientes: ClienteBackend[] = await this.request('/api/clientes');
    return clientes.map(cliente => this.mapClienteFromBackend(cliente));
  }

  async getCliente(id: number): Promise<Cliente | null> {
    try {
      const cliente: ClienteBackend = await this.request(`/api/clientes/${id}`);
      return this.mapClienteFromBackend(cliente);
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      return null;
    }
  }

  async createCliente(cliente: Omit<Cliente, 'id'>): Promise<Cliente> {
    const clienteData = this.mapClienteToBackend(cliente);
    const response: { id: number } = await this.request('/api/clientes', {
      method: 'POST',
      body: JSON.stringify(clienteData),
    });

    return {
      ...cliente,
      id: response.id,
      dataCadastro: new Date().toISOString().split('T')[0],
    };
  }

  async updateCliente(id: number, cliente: Partial<Cliente>): Promise<Cliente | null> {
    try {
      const clienteData = this.mapClienteToBackend(cliente as any);
      await this.request(`/api/clientes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(clienteData),
      });

      // Buscar o cliente atualizado
      return await this.getCliente(id);
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      return null;
    }
  }

  async deleteCliente(id: number): Promise<boolean> {
    try {
      await this.request(`/api/clientes/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Erro ao deletar cliente:', error);
      return false;
    }
  }

  async buscarCNPJ(cnpj: string): Promise<any> {
    try {
      const data = await this.request(`/api/clientes/cnpj/${cnpj}`);
      return data;
    } catch (error) {
      console.error('Erro ao buscar CNPJ:', error);
      throw error;
    }
  }

  // Formula methods
  async getFormulas(): Promise<FormulaPigmentacao[]> {
    const formulas: FormulaBackend[] = await this.request('/api/formulas');
    return formulas.map(formula => this.mapFormulaFromBackend(formula));
  }

  async createFormula(formula: Omit<FormulaPigmentacao, 'id'>): Promise<FormulaPigmentacao> {
    const formulaData = {
      nome: formula.nome,
      codigo: `FORM${Date.now()}`,
      cor_final_hex: formula.corFinal,
      descricao: formula.descricao,
      foto_amostra: formula.fotoAmostra || null,
      rendimento: 3.6,
      unidade_rendimento: 'L',
      preco_sugerido: 100,
      pigmentos: formula.pigmentos.map(p => ({
        pigmento_id: p.pigmentoId,
        quantidade: p.quantidade,
        unidade: p.unidade,
        custo_item: 10 // Valor padrão
      }))
    };

    const response: { id: number } = await this.request('/api/formulas', {
      method: 'POST',
      body: JSON.stringify(formulaData),
    });

    return {
      ...formula,
      id: response.id,
      dataCriacao: new Date().toISOString().split('T')[0],
    };
  }

  async updateFormula(id: number, formula: Partial<FormulaPigmentacao>): Promise<FormulaPigmentacao | null> {
    try {
      const formulaData = {
        nome: formula.nome,
        codigo: `FORM${id}`,
        cor_final_hex: formula.corFinal,
        descricao: formula.descricao,
        foto_amostra: formula.fotoAmostra || null,
        rendimento: 3.6,
        unidade_rendimento: 'L',
        preco_sugerido: 100,
        pigmentos: formula.pigmentos?.map(p => ({
          pigmento_id: p.pigmentoId,
          quantidade: p.quantidade,
          unidade: p.unidade,
          custo_item: 10
        })) || []
      };

      await this.request(`/api/formulas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(formulaData),
      });

      // Buscar a fórmula atualizada
      const formulaAtualizada: FormulaBackend = await this.request(`/api/formulas/${id}`);
      return this.mapFormulaFromBackend(formulaAtualizada);
    } catch (error) {
      console.error('Erro ao atualizar fórmula:', error);
      return null;
    }
  }

  async deleteFormula(id: number): Promise<boolean> {
    try {
      await this.request(`/api/formulas/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Erro ao deletar fórmula:', error);
      return false;
    }
  }

  // Pigmento methods (novo)
  async getPigmentos(): Promise<Pigmento[]> {
    const pigmentos: PigmentoBackend[] = await this.request('/api/pigmentos');
    return pigmentos.map(pigmento => ({
      id: pigmento.id,
      nome: pigmento.nome,
      cor: pigmento.cor_hex || '#000000',
      codigo: pigmento.codigo,
    }));
  }

  // Pigmento Detalhado methods (com notas fiscais e caixas/lotes)
  async getPigmentosDetalhados(): Promise<PigmentoDetalhado[]> {
    const pigmentos: PigmentoDetalhado[] = await this.request('/api/pigmentos');
    return pigmentos;
  }

  async getPigmentoDetalhado(id: number): Promise<PigmentoDetalhado | null> {
    try {
      const pigmento: PigmentoDetalhado = await this.request(`/api/pigmentos/${id}`);
      return pigmento;
    } catch (error) {
      console.error('Erro ao buscar pigmento:', error);
      return null;
    }
  }

  async createPigmentoDetalhado(pigmento: Omit<PigmentoDetalhado, 'id' | 'created_at' | 'updated_at' | 'ativo'>): Promise<PigmentoDetalhado> {
    const response: { id: number } = await this.request('/api/pigmentos', {
      method: 'POST',
      body: JSON.stringify(pigmento),
    });

    return {
      ...pigmento,
      id: response.id,
      ativo: 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
  }

  async updatePigmentoDetalhado(id: number, pigmento: Partial<PigmentoDetalhado>): Promise<PigmentoDetalhado | null> {
    try {
      await this.request(`/api/pigmentos/${id}`, {
        method: 'PUT',
        body: JSON.stringify(pigmento),
      });

      // Buscar o pigmento atualizado
      return await this.getPigmentoDetalhado(id);
    } catch (error) {
      console.error('Erro ao atualizar pigmento:', error);
      return null;
    }
  }

  async deletePigmentoDetalhado(id: number): Promise<boolean> {
    try {
      await this.request(`/api/pigmentos/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Erro ao deletar pigmento:', error);
      return false;
    }
  }

  // Movimento Financeiro methods
  async getMovimentos(): Promise<MovimentoFinanceiro[]> {
    const movimentos: MovimentacaoBackend[] = await this.request('/api/movimentacoes');
    return movimentos.map(mov => this.mapMovimentacaoFromBackend(mov));
  }

  async createMovimento(movimento: Omit<MovimentoFinanceiro, 'id'>): Promise<MovimentoFinanceiro> {
    const movimentoData = {
      descricao: movimento.descricao,
      valor: movimento.valor,
      tipo: movimento.tipo,
      data_movimento: movimento.data,
      categoria_id: null, // Seria necessário mapear categorias
      status: 'pago'
    };

    const response: { id: number } = await this.request('/api/movimentacoes', {
      method: 'POST',
      body: JSON.stringify(movimentoData),
    });

    return {
      ...movimento,
      id: response.id,
    };
  }

  async updateMovimento(id: number, movimento: Partial<MovimentoFinanceiro>): Promise<MovimentoFinanceiro | null> {
    try {
      const movimentoData = {
        descricao: movimento.descricao,
        valor: movimento.valor,
        tipo: movimento.tipo,
        data_movimento: movimento.data,
        categoria_id: null,
        status: 'pago'
      };

      await this.request(`/api/movimentacoes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(movimentoData),
      });

      const movAtualizado: MovimentacaoBackend = await this.request(`/api/movimentacoes/${id}`);
      return this.mapMovimentacaoFromBackend(movAtualizado);
    } catch (error) {
      console.error('Erro ao atualizar movimento:', error);
      return null;
    }
  }

  async deleteMovimento(id: number): Promise<boolean> {
    try {
      await this.request(`/api/movimentacoes/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Erro ao deletar movimento:', error);
      return false;
    }
  }

  // Orçamento methods
  async getOrcamentos(): Promise<Orcamento[]> {
    const orcamentos: OrcamentoBackend[] = await this.request('/api/orcamentos');
    return orcamentos.map(orc => this.mapOrcamentoFromBackend(orc));
  }

  async getOrcamentoById(id: number): Promise<Orcamento> {
    const orcamento: OrcamentoBackend = await this.request(`/api/orcamentos/${id}`);
    return this.mapOrcamentoFromBackend(orcamento);
  }

  async createOrcamento(orcamento: Omit<Orcamento, 'id'>): Promise<Orcamento> {
    const orcamentoData = {
      numero: `ORC${Date.now()}`,
      cliente_id: orcamento.clienteId,
      data_orcamento: orcamento.dataOrcamento,
      data_validade: orcamento.dataVencimento,
      desconto: 0,
      observacoes: orcamento.observacoes,
      itens: orcamento.itens.map(item => ({
        descricao: item.descricao,
        quantidade: item.quantidade,
        unidade: 'un',
        preco_unitario: item.valorUnitario,
        subtotal: item.valorTotal
      }))
    };

    const response: { id: number } = await this.request('/api/orcamentos', {
      method: 'POST',
      body: JSON.stringify(orcamentoData),
    });

    return {
      ...orcamento,
      id: response.id,
    };
  }

  async updateOrcamento(id: number, orcamento: Partial<Orcamento>): Promise<Orcamento | null> {
    try {
      const orcamentoData = {
        numero: `ORC${id}`,
        cliente_id: orcamento.clienteId,
        data_orcamento: orcamento.dataOrcamento,
        data_validade: orcamento.dataVencimento,
        status: orcamento.status === 'pedido' ? 'aprovado' : orcamento.status,
        desconto: 0,
        observacoes: orcamento.observacoes,
        itens: orcamento.itens?.map(item => ({
          descricao: item.descricao,
          quantidade: item.quantidade,
          unidade: 'un',
          preco_unitario: item.valorUnitario,
          subtotal: item.valorTotal
        })) || []
      };

      await this.request(`/api/orcamentos/${id}`, {
        method: 'PUT',
        body: JSON.stringify(orcamentoData),
      });

      const orcAtualizado: OrcamentoBackend = await this.request(`/api/orcamentos/${id}`);
      return this.mapOrcamentoFromBackend(orcAtualizado);
    } catch (error) {
      console.error('Erro ao atualizar orçamento:', error);
      return null;
    }
  }

  async deleteOrcamento(id: number): Promise<boolean> {
    try {
      await this.request(`/api/orcamentos/${id}`, {
        method: 'DELETE',
      });
      return true;
    } catch (error) {
      console.error('Erro ao deletar orçamento:', error);
      return false;
    }
  }

  // Dashboard stats
  async getDashboardStats() {
    try {
      const stats: any = await this.request('/api/dashboard/stats');
      return {
        totalClientes: stats.clientes?.total || 0,
        totalOrcamentos: stats.orcamentos?.total || 0,
        totalReceitaMes: stats.financeiro?.receitas || 0,
        orcamentosPendentes: (stats.orcamentos?.total || 0) - (stats.orcamentos?.aprovados || 0),
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      return {
        totalClientes: 0,
        totalOrcamentos: 0,
        totalReceitaMes: 0,
        orcamentosPendentes: 0,
      };
    }
  }
}

export const apiService = new ApiService();